import { memo, useMemo } from 'react';
import { Tabs, Tab } from '@ds';
import { Channel, UserResponse } from 'stream-chat';
import { ConditionalRender } from '@ui';
import { ChatMediaTab } from './ChatMediaTab/ChatMediaTab';
import { ChatFilesTab } from './ChatFilesTab/ChatFilesTab';
import { ChatMembersTab } from './ChatMembersTab/ChatMembersTab';
import { ChatDetailsHeader } from './ChatDetailsHeader';
import { getChannelMembers } from '../Messages/utils/getChannelMembers';

type ChatDetailsPanelProps = {
  channel: Channel;
  onClose: () => void;
  onMentionUser?: (user: UserResponse) => void;
  inlineLayout?: boolean;
};

export const ChatDetailsPanel = memo(
  ({ channel, onClose, onMentionUser, inlineLayout = false }: ChatDetailsPanelProps) => {
    const chatUsers = useMemo(() => getChannelMembers(channel), [channel]);

    return (
      <div className="h-full flex flex-col scroll no-track">
        <button type="button" aria-label="Close chat details panel" onClick={onClose} className="sr-only">
          Close
        </button>
        <ConditionalRender show={inlineLayout}>
          <div className="shrink-0">
            <ChatDetailsHeader channel={channel} />
          </div>
        </ConditionalRender>
        <div
          className={
            inlineLayout ? 'mx-auto w-[750px] flex-1 flex flex-col min-h-0 pb-4' : 'w-full flex-1 flex flex-col min-h-0'
          }
        >
          <div className="bg-base-90 rounded-xl py-2 flex-1 flex flex-col min-h-0">
            <Tabs
              fullWidth
              aria-label="Chat Details Tabs"
              variant="underlined"
              classNames={{
                base: 'flex flex-col flex-1',
                tabList: 'sticky top-0 z-10 bg-base-90 border-b border-base-55 px-3 gap-0 w-full rounded-none shrink-0',
                tab: 'h-8 px-4 py-1 uppercase',
                tabContent: 'text-s-cps group-data-[selected=true]:text-base-5 text-base-20',
                panel: 'px-3 pt-4 pb-2 h-full overflow-y-auto',
                cursor: 'bg-base-5 h-[1.5px]',
              }}
            >
              <Tab key="members" title="Members">
                <ChatMembersTab chatUsers={chatUsers} onMentionUser={onMentionUser} />
              </Tab>
              <Tab key="media" title="Media">
                <ChatMediaTab inlineLayout={inlineLayout} />
              </Tab>
              <Tab key="files" title="Files">
                <ChatFilesTab />
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
    );
  },
);
